import React, { useState } from "react";
import {
  Typography,
  Box,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Stack,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

export default function CartSummary({
  cartItems,
  walletAmount,
  reviewMessage,
  closeIconShow = false,
  setOrderSummaryVisibility,
}) {
  const [expandedItems, setExpandedItems] = useState([]);

  // Helper function to handle accordion expansion
  const handleAccordionChange = (itemId) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Calculate totals for each category across all items
  const grantTotals = cartItems.reduce(
    (totals, item) => {
      return {
        mrp:
          totals.mrp +
          item.totalFramePrice +
          item.totalLensPrice +
          item.totalOtherPrice,
        discount:
          totals.discount +
          item.totalFrameDiscount +
          item.totalLensDiscount +
          item.totalOtherDiscount,
        net:
          totals.net +
          (item.totalFramePrice - item.totalFrameDiscount) +
          (item.totalLensPrice - item.totalLensDiscount) +
          (item.totalOtherPrice - item.totalOtherDiscount),
      };
    },
    {
      mrp: 0,
      discount: 0,
      net: 0,
    }
  );

  // Format currency function
  const formatCurrency = (amount) => {
    return "₹" + parseFloat(amount).toFixed(2);
  };

  return (
    <Box sx={{ p: 2 }}>
      <div className="flex justify-between">
        <div className="flex flex-col">
          <Typography variant="h6" gutterBottom>
            Order Summary
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Please review your order details before proceeding to payment.
          </Typography>
        </div>
        {closeIconShow && (
          <div
            className="text-gray-500"
            onClick={() => setOrderSummaryVisibility(false)}
          >
            <span className="cursor-pointer">
              <CloseIcon></CloseIcon>
            </span>
          </div>
        )}
      </div>

      {/* Items Breakdown */}
      <Box sx={{ mt: 3, mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Items ({cartItems.length})
        </Typography>

        {cartItems.map((item, index) => (
          <Accordion
            key={item.itemId}
            expanded={expandedItems.includes(item.itemId)}
            onChange={() => handleAccordionChange(item.itemId)}
            sx={{ mb: 2, border: "1px solid #e0e0e0", boxShadow: "none" }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`item-${item.itemId}-content`}
              id={`item-${item.itemId}-header`}
              sx={{ backgroundColor: "#fafafa" }}
            >
              <Grid container alignItems="center">
                <Grid item xs={7}>
                  <Typography variant="subtitle2">{item.itemName}</Typography>
                </Grid>
                <Grid item xs={5} sx={{ textAlign: "right" }}>
                  <Typography variant="subtitle2" fontWeight="medium">
                    {formatCurrency(item.totalPrice - item.totalDiscount)}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip
                  label={item.category}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                {item.lensType && (
                  <Chip label={item.lensType} size="small" variant="outlined" />
                )}
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell>Component</TableCell>
                      <TableCell align="right">MRP</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="right">Net</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Frame
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.totalFramePrice)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.totalFrameDiscount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(
                          item.totalFramePrice - item.totalFrameDiscount
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Lens
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.totalLensPrice)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.totalLensDiscount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(
                          item.totalLensPrice - item.totalLensDiscount
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Other
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.totalOtherPrice)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.totalOtherDiscount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(
                          item.totalOtherPrice - item.totalOtherDiscount
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: "#fafafa" }}>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{ fontWeight: "bold" }}
                      >
                        Item Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(item.totalPrice)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(item.totalDiscount)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(item.totalPrice - item.totalDiscount)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Grand Total */}
      <Card
        variant="outlined"
        sx={{ mt: 4, backgroundColor: "#f8f9fa", borderColor: "#c2d6f5" }}
      >
        <CardContent>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: "bold" }}
          >
            Order Total
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Total MRP:</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" align="right">
                {formatCurrency(grantTotals.mrp)}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Total Discount:</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" align="right">
                {formatCurrency(grantTotals.discount)}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Net Amount:</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" align="right">
                {formatCurrency(grantTotals.net)}
              </Typography>
            </Grid>

            {walletAmount > -1 && (
              <>
                <hr></hr>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Wallet Amount:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold" }}
                    align="right"
                  >
                    {formatCurrency(walletAmount)}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Amount To Pay:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold" }}
                    align="right"
                  >
                    {formatCurrency(grantTotals.net - walletAmount)}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>
      {reviewMessage && (
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              sx={{
                backgroundColor: "#fffdd0",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #cdc402",
                marginTop: "20px",
              }}
            >
              {reviewMessage}
            </Typography>
          </Grid>
        )}
    </Box>
  );
}
